import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, KeyRound, Loader2, ShieldAlert } from 'lucide-react';
import { studentApi } from './journey/journeyApi';

const input = 'w-full bg-white/10 border border-white/20 rounded-full px-5 py-3.5 text-white placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-teal-400';

export default function ActivateAccount() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = 'Activate your account | Elevate Careers';
    studentApi(`/auth/activate/${encodeURIComponent(token)}`).then(setInfo).catch((e) => setError(e.message));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError('Passwords do not match'); return; }
    setBusy(true); setError('');
    try {
      await studentApi('/auth/activate', { method: 'POST', body: { token, password } });
      navigate('/my-journey', { replace: true });
    } catch (err) { setError(err.message); setBusy(false); }
  };

  return (
    <main className="min-h-[80vh] bg-[#0b1f4d] text-white flex items-center py-16" data-testid="activate-page">
      <div className="w-full max-w-md mx-auto px-5">
        <div className="w-14 h-14 rounded-2xl bg-teal-400 flex items-center justify-center mb-6"><KeyRound className="w-7 h-7 text-[#0b1f4d]" /></div>
        {!info && !error && <Loader2 className="w-6 h-6 animate-spin text-teal-300" data-testid="activate-loading" />}
        {!info && error && (
          <div data-testid="activate-invalid">
            <h1 className="font-outfit text-3xl font-bold">This link can't be used</h1>
            <p className="mt-3 text-slate-300 flex gap-2"><ShieldAlert className="w-5 h-5 text-amber-300 shrink-0" />{error}</p>
            <a href="/my-journey" className="inline-block mt-8 text-teal-300 font-semibold" data-testid="activate-go-signin">Go to sign in →</a>
          </div>
        )}
        {info && (
          <>
            <p className="text-[11px] font-semibold tracking-[0.25em] uppercase text-teal-300">{info.kind === 'invite' ? 'Account activation' : 'Dashboard login'}</p>
            <h1 className="font-outfit text-3xl sm:text-4xl font-bold mt-2" data-testid="activate-title">{info.first_name ? `Welcome, ${info.first_name}` : 'Welcome'}</h1>
            <p className="mt-3 text-slate-300">Choose a password for <span className="font-semibold text-white" data-testid="activate-email-hint">{info.email_hint}</span>. Your existing profile and progress are already saved, so there's nothing to fill in again.</p>
            <form onSubmit={submit} className="mt-8 space-y-3" data-testid="activate-form">
              <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password (8+ characters)" className={input} autoComplete="new-password" data-testid="activate-password" />
              <input type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm password" className={input} autoComplete="new-password" data-testid="activate-password-confirm" />
              {error && <p className="text-sm text-amber-300" data-testid="activate-error">{error}</p>}
              <button disabled={busy || password.length < 8} className="w-full inline-flex items-center justify-center gap-2 bg-teal-400 hover:bg-teal-300 disabled:opacity-60 text-[#0b1f4d] font-semibold py-3.5 rounded-full transition-colors" data-testid="activate-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Activate & open my dashboard
              </button>
            </form>
          </>
        )}
      </div>
    </main>
  );
}
